import Customer from "@/redux/models/Customer";
import { Switch } from "@headlessui/react";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { toast } from "react-toastify";

export const ProfileDetails = () => {
  const user = useSelector(Customer.selectors.getCustomer);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordMatchError, setPasswordMatchError] = useState(false);
  const [customerData, setCustomerData] = useState(user);
  const [phoneValid, setPhoneValid] = useState(true);
  const dispatch = useDispatch();

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerData.email);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setCustomerData({ ...customerData, [name]: value });
  };

  const handleUpdateCustomer = async (event) => {
    event.preventDefault();
    if (customerData.phone && !phoneValid) {
      toast.error("Invalid Phone Number");
      return;
    }

    dispatch(Customer.types.updateCustomerDetail(customerData));
  };

  const handleNewPasswordChange = (event) => {
    setNewPassword(event.target.value);
  };

  const handleConfirmPasswordChange = (event) => {
    setConfirmPassword(event.target.value);
    setPasswordMatchError(newPassword !== event.target.value);
  };

  const handleUpdatePassword = async (event) => {
    event.preventDefault();
    const currentPassword = document.getElementById("current-password").value;
    document.getElementById("current-password").value = "";
    dispatch(
      Customer.types.updatePassword({
        old_password: currentPassword,
        new_password: newPassword,
      })
    );

    setNewPassword("");
    setConfirmPassword("");
  };

  useEffect(() => {
    dispatch(
      Customer.types.retrieveCustomerDetail({
        cb: (newUser) => setCustomerData(newUser),
      })
    );
  }, []);

  return (
    <form
      className="mb-8 divide-y divide-gray-200 lg:col-span-10"
      action="#"
      method="POST"
    >
      <div className="px-4 py-6 sm:p-6 lg:pb-8">
        <h2 className="text-lg font-medium leading-6 text-gray-900">Profile</h2>

        <div className="grid grid-cols-12 gap-6 mt-6">
          <div className="col-span-12 sm:col-span-5">
            <div>
              <label
                htmlFor="fullName"
                className="block text-sm font-medium text-gray-700"
              >
                Full Name
              </label>
              <input
                type="text"
                name="name"
                id="fullName"
                value={customerData.name}
                autoComplete="name"
                onChange={handleInputChange}
                className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:border-sky-500 focus:outline-none focus:ring-sky-500 sm:text-sm"
              />
            </div>
            <div className="mt-6">
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700"
              >
                Phone
              </label>
              <PhoneInput
                id="phone"
                name="phone"
                className={classNames(
                  "custom-phone-input",
                  !phoneValid && "!border-red-600"
                )}
                defaultCountry="US"
                value={customerData.phone}
                onChange={(newVal) => {
                  setCustomerData({ ...customerData, phone: newVal });
                  setPhoneValid(isValidPhoneNumber(newVal || ""));
                }}
              />
              {!phoneValid && (
                <div className="text-xs text-red-600">Invalid phone number</div>
              )}
            </div>
          </div>

          <div className="col-span-12 sm:col-span-5">
            <label
              htmlFor="company"
              className="block text-sm font-medium text-gray-700"
            >
              Company
            </label>
            <input
              type="text"
              name="company"
              id="company"
              autoComplete="organization"
              onChange={handleInputChange}
              className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:border-sky-500 focus:outline-none focus:ring-sky-500 sm:text-sm"
              value={customerData.company}
            />
          </div>
          <div className="flex items-start justify-end h-full col-span-12 sm:col-span-2">
            <button
              className="mt-auto blue-button sm:w-full"
              onClick={handleUpdateCustomer}
            >
              Update
            </button>
          </div>
        </div>
      </div>

      <div className="pt-6 divide-y divide-gray-200">
        <div className="px-4 sm:px-6">
          <h2 className="text-lg font-medium leading-6 text-gray-900">
            Contact Preferences
          </h2>
          <div className="mt-2">
            <div className="grid grid-cols-12 gap-6 mt-6">
              <div className="col-span-12 sm:col-span-10">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  autoComplete="email"
                  onChange={handleInputChange}
                  value={customerData.email}
                  className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:border-sky-500 focus:outline-none focus:ring-sky-500 sm:text-sm"
                />
              </div>

              <div className="flex items-start justify-end h-full col-span-12 sm:col-span-2">
                <button
                  disabled={!isEmailValid}
                  className="mt-auto blue-button sm:w-full"
                  onClick={handleUpdateCustomer}
                >
                  Update
                </button>
              </div>
            </div>

            <Switch.Group
              as="li"
              className="flex items-center justify-between py-4"
            >
              <div className="flex flex-col">
                <Switch.Label
                  as="p"
                  className="text-sm font-medium text-gray-900"
                  passive
                >
                  Receive Order Updates
                </Switch.Label>
              </div>
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-900">Email</span>
                <Switch
                  checked={customerData.notify}
                  onChange={() => {
                    setCustomerData({
                      ...customerData,
                      notify: !customerData.notify,
                    });
                    dispatch(
                      Customer.types.updateCustomerDetail({
                        notify: !customerData.notify,
                      })
                    );
                  }}
                  className={classNames(
                    customerData.notify ? "bg-blue-500" : "bg-gray-200",
                    "relative ml-4 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={classNames(
                      customerData.notify ? "translate-x-5" : "translate-x-0",
                      "inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                    )}
                  />
                </Switch>
              </div>
            </Switch.Group>

            <Switch.Group
              as="li"
              className="flex items-center justify-between py-4"
            >
              <div className="flex flex-col">
                <Switch.Label
                  as="p"
                  className="text-sm font-medium text-gray-900"
                  passive
                >
                  Receive Marketing Updates
                </Switch.Label>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center">
                  <span className="text-sm font-medium text-gray-900">
                    Email
                  </span>
                  <Switch
                    checked={customerData.marketing_email_enabled}
                    onChange={() => {
                      setCustomerData({
                        ...customerData,
                        marketing_email_enabled:
                          !customerData.marketing_email_enabled,
                      });
                      dispatch(
                        Customer.types.updateCustomerDetail({
                          marketing_email_enabled:
                            !customerData.marketing_email_enabled,
                        })
                      );
                    }}
                    className={classNames(
                      customerData.marketing_email_enabled
                        ? "bg-blue-500"
                        : "bg-gray-200",
                      "relative ml-4 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={classNames(
                        customerData.marketing_email_enabled
                          ? "translate-x-5"
                          : "translate-x-0",
                        "inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                      )}
                    />
                  </Switch>
                </div>
                <div className="flex items-center">
                  <span className="text-sm font-medium text-gray-900">SMS</span>
                  <Switch
                    checked={customerData.marketing_sms_enabled}
                    onChange={() => {
                      setCustomerData({
                        ...customerData,
                        marketing_sms_enabled:
                          !customerData.marketing_sms_enabled,
                      });
                      dispatch(
                        Customer.types.updateCustomerDetail({
                          marketing_sms_enabled:
                            !customerData.marketing_sms_enabled,
                        })
                      );
                    }}
                    className={classNames(
                      customerData.marketing_sms_enabled
                        ? "bg-blue-500"
                        : "bg-gray-200",
                      "relative ml-4 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={classNames(
                        customerData.marketing_sms_enabled
                          ? "translate-x-5"
                          : "translate-x-0",
                        "inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                      )}
                    />
                  </Switch>
                </div>
              </div>
            </Switch.Group>
          </div>

          <div className="flex items-start justify-end h-full col-span-12 sm:col-span-2"></div>
        </div>
      </div>

      <div className="px-4 py-6 sm:p-6 lg:pb-8">
        <h2 className="text-lg font-medium leading-6 text-gray-900">
          Login Information
        </h2>

        <div className="grid grid-cols-12 gap-6 mt-6">
          <div className="col-span-12 sm:col-span-10">
            <label
              htmlFor="current-password"
              className="block text-sm font-medium text-gray-700"
            >
              Current Password
            </label>
            <input
              type="password"
              name="current-password"
              id="current-password"
              autoComplete="off"
              className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:border-sky-500 focus:outline-none focus:ring-sky-500 sm:text-sm"
            />
          </div>

          <div className="col-span-12 sm:col-span-5">
            <label
              htmlFor="new-password"
              className="block text-sm font-medium text-gray-700"
            >
              New Password
            </label>
            <input
              type="password"
              name="new-password"
              id="new-password"
              value={newPassword}
              onChange={handleNewPasswordChange}
              autoComplete="password"
              className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:border-sky-500 focus:outline-none focus:ring-sky-500 sm:text-sm"
            />
          </div>

          <div className="col-span-12 sm:col-span-5">
            <label
              htmlFor="confirm-password"
              className="block text-sm font-medium text-gray-700"
            >
              Confirm Password
            </label>
            <input
              type="password"
              name="confirm-password"
              id="confirm-password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              autoComplete="password"
              className="block w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:border-sky-500 focus:outline-none focus:ring-sky-500 sm:text-sm"
            />
            {passwordMatchError && (
              <p className="text-sm text-red-500">Passwords do not match.</p>
            )}
          </div>

          <div className="flex items-start justify-end h-full col-span-12 sm:col-span-2">
            <button
              className="mt-auto blue-button sm:w-full"
              disabled={passwordMatchError}
              onClick={handleUpdatePassword}
            >
              Update
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};
